/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IBreadcrumbViewProps } from '@msdyn365-commerce-modules/breadcrumb';

const BreadCrumbView: React.FC<IBreadcrumbViewProps> = props => {
    const { root, links, backButton, BreadcrumbContainer, BreadcrumbList, BreadcrumbListItem } = props;
    if (!root && !links && !backButton) {
        props.context.telemetry.error('BreadCrumb content is empty, module wont render');
        return null;
    }

    React.useEffect(() => {
        const links = document.querySelectorAll('.ms-breadcrumb_link');
        const newOnClick = (event: Event) => {
            // Check if function exists, if not behave as a normal link. Otherwise use the function to handle navigation,
            // preventing a full page reload.
            if (window.onSelectCategory === undefined) {
                return;
            }

            // Get category record id from href using regex
            const target = event.currentTarget as HTMLAnchorElement;
            const regex = /\/(\d+)\.c/;
            const match = target.href.match(regex);
            if (match && match[1]) {
                event.preventDefault();
                const categoryId = match[1];
                window.onSelectCategory?.(parseInt(categoryId, 10), true);
            }
        };

        links.forEach(link => {
            link.addEventListener('click', newOnClick);
        });

        return () => {
            links.forEach(link => {
                link.removeEventListener('click', newOnClick);
            });
        };
    }, []);

    const separator = <span className='ms-breadcrumb__separator' aria-hidden></span>;

    return (
        <Module {...BreadcrumbContainer}>
            <Node {...BreadcrumbList}>
                {root && (
                    <Node {...BreadcrumbListItem}>
                        {root}
                        {links && separator}
                    </Node>
                )}
                {backButton && <Node {...BreadcrumbListItem}>{backButton}</Node>}
                {links &&
                    links.map((link, index) => {
                        const linkText = React.isValidElement(link) && typeof link.props.children === 'string'
                            ? link.props.children as string : undefined;

                        if (linkText) {
                            link = React.cloneElement(link as React.ReactElement, {
                                children: linkText.replace('{q}', props.context.request.query?.q || '')
                            });
                        }

                        return (
                            <React.Fragment key={index}>
                                <Node
                                    {...BreadcrumbListItem}
                                    className={classnames(
                                        BreadcrumbListItem.className,
                                        index === links.length - 1 ? 'breadcrumb_item-current' : ''
                                    )}
                                >
                                    {link}
                                    {links[index + 1] && separator}
                                </Node>
                            </React.Fragment>
                        );
                    })}
            </Node>
        </Module>
    );
};

export default BreadCrumbView;
